/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  AdminApi,
  ConfigApi,
  EmployeesApi,
  PatientsApi,
  TemplatesApi,
  UsersApi
} from '../apis';
import { ToastContext } from '../contexts';
import { IToastOptionsData } from '../../config/interfaces';

/**
 * Redux slice for managing toast notification state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to displaying, hiding, and configuring toast notifications, including:
 * - Showing and hiding toast notifications with slide animations
 * - Setting toast options such as message, duration, and appearance
 * - Setting the type of toast (e.g., success, error, info)
 * - Managing the hidden and slide state for toast visibility and transitions
 *
 * @property {object} initialState - The initial state for the toast context.
 * @property {Function} slideIn - Shows the toast notification with a slide-in animation.
 * @property {Function} slideOut - Hides the toast notification with a slide-out animation.
 * @property {Function} hide - Fully hides the toast and resets its options and type.
 * @property {Function} setToastOptions - Sets the options for the toast notification (e.g., message, duration).
 * @property {Function} setToastType - Sets the type of the toast notification (e.g., success, error).
 *
 * @example
 * dispatch(slideIn());
 * dispatch(setToastOptions({ message: 'Saved!', duration: 3000 }));
 * dispatch(setToastType('success'));
 * dispatch(slideOut());
 * dispatch(hide());

 * @returns {object} The toast slice reducer and actions.
 */
const toastSlice = createSlice({
  name: 'toast',
  initialState: ToastContext,
  reducers: {
    slideIn: state => {
      return { ...state, hidden: false, slide: 'show' };
    },
    slideOut: state => {
      return { ...state, slide: 'hide' };
    },
    hide: state => {
      return {
        ...state,
        hidden: true,
        options: {} as IToastOptionsData,
        type: ''
      };
    },
    setToastOptions: (state, action: PayloadAction<any>) => {
      return { ...state, options: { ...action.payload } };
    },
    setToastType: (state, action: PayloadAction<string>) => {
      return { ...state, type: action.payload };
    }
  },
  extraReducers: builder => {
    // Admin endpoint toasts
    builder.addMatcher(AdminApi.endpoints.getUsers.matchRejected, state => {
      return {
        ...state,
        hidden: false,
        slide: 'show',
        options: {
          msg: `Failed to retrieve users!`,
          feedback: 'error',
          duration: 1200,
          speed: 350,
          question: false
        }
      };
    });
    builder.addMatcher(
      AdminApi.endpoints.postRevokeUserAccess.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Access Revoked!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      AdminApi.endpoints.postRevokeUserAccess.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Failed to revoke access!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      AdminApi.endpoints.postDischargePatients.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Successfully discharged patients!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      AdminApi.endpoints.postDischargePatients.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Failed to discharge patients!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );

    // Config endpoint toasts
    builder.addMatcher(
      ConfigApi.endpoints.postUnitLocTypeAndFlag.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Updated Unit!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      ConfigApi.endpoints.postUnitLocTypeAndFlag.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Failed to update!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );

    // Employee endpoint toasts
    builder.addMatcher(
      isAnyOf(
        EmployeesApi.endpoints.postAddEmployeeNote.matchFulfilled,
        PatientsApi.endpoints.postAddPatientNote.matchFulfilled
      ),
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: 'Note Posted!',
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      isAnyOf(
        EmployeesApi.endpoints.postAddEmployeeNote.matchRejected,
        PatientsApi.endpoints.postAddPatientNote.matchRejected
      ),
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: 'Note Failed to Post!',
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      isAnyOf(
        EmployeesApi.endpoints.postRemoveEmployeeNote.matchFulfilled,
        PatientsApi.endpoints.postRemovePatientNote.matchFulfilled
      ),
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: 'Note Removed!',
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      isAnyOf(
        EmployeesApi.endpoints.postRemoveEmployeeNote.matchRejected,
        PatientsApi.endpoints.postRemovePatientNote.matchRejected
      ),
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: 'Failed to Remove Note!',
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );

    // Template endpoint toasts
    builder.addMatcher(
      TemplatesApi.endpoints.postMasterTemplate.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: 'Master Template saved!',
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      TemplatesApi.endpoints.postMasterTemplate.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: 'Failed to save Master Template!',
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      TemplatesApi.endpoints.postTemplateConfig.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: 'Template config saved!',
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      TemplatesApi.endpoints.postTemplateConfig.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: 'Failed to save Template config!',
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );

    // User endpoint toasts
    builder.addMatcher(
      UsersApi.endpoints.postAddEmployeeToUserFavs.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `AdHoc employee added!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postAddEmployeeToUserFavs.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `AdHoc employee failed to Add!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postUpdateCurrentFacility.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Facility updated!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postUpdateCurrentFacility.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Failed to Update Facility!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postRemoveEmployeeFromUserFavs.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `AdHoc employee removed!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postRemoveEmployeeFromUserFavs.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `AdHoc employee failed to Remove!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postAddFavoriteUnit.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Favorite unit added!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postAddFavoriteUnit.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Favorite failed to Post!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postRemoveFavoriteUnit.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Favorite unit removed!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postRemoveFavoriteUnit.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Failed to Remove Favorite!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postAddDelegateToUser.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `'Delegate Added!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postAddDelegateToUser.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Delegate failed to Add!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postRemoveDelegateFromUser.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Delegate Removed!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postRemoveDelegateFromUser.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Failed to remove Delegate!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postRemoveUserAsDelegate.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `User Removed!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postRemoveUserAsDelegate.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Failed to remove User!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.updateSubjectHiddenStatus.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Successfully updated subject!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.updateSubjectHiddenStatus.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Failed to update subject!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postUpdateRoundingListConfig.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Rounding config updated!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postUpdateRoundingListConfig.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Failed to update rounding config!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postUserAccess.matchFulfilled,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Access Granted!`,
            feedback: 'success',
            duration: 1000,
            speed: 500,
            question: false
          }
        };
      }
    );
    builder.addMatcher(
      UsersApi.endpoints.postUserAccess.matchRejected,
      state => {
        return {
          ...state,
          hidden: false,
          slide: 'show',
          options: {
            msg: `Failed to grant access!`,
            feedback: 'error',
            duration: null,
            speed: 500,
            question: true
          }
        };
      }
    );
  }
});

export const { slideIn, slideOut, hide, setToastOptions, setToastType } =
  toastSlice.actions;

export default toastSlice.reducer;
